import React from 'react';
import { useLocation } from 'react-router-dom';

import { AdminListPageConfig, AdminListPageRenderer, TabDef, useAdminListPage } from 'hooks/useAdminListPage';
import { simpleDateAtTime } from 'lib/date';
import type { Client } from 'models/flexible/Client';
import { ClientType } from 'models/flexible/Client';
import { Collaboration } from 'models/flexible/Collaboration';
import { GroupType } from 'models/flexible/GroupType';
import { TypedImportConfigurationIndex } from 'models/flexible/ImportConfiguration';
import { adminClientsQueryKeys, adminIndexClients } from 'services/api/admin/clients';
import { adminCollaborationsQueryKeys, adminIndexCollaborations } from 'services/api/admin/collaborations';
import { indexGroupTypes } from 'services/api/admin/groupTypes';
import {
  adminImportConfigurationsQueryKeys,
  adminIndexImportConfigurations,
} from 'services/api/admin/importConfigurations';
import type { AdminClientIndexFields } from 'services/api/fields';
import type { AdminCollaborationIndexFields } from 'services/api/fields';
import type { GroupTypeFields } from 'services/api/fields';
import AcceptedPlansIcon from 'svg/AcceptedPlansIcon';
import CollaborationsIcon from 'svg/CollaborationsIcon';
import ConfigureCareIcon from 'svg/ConfigureCareIcon';
import PencilIcon from 'svg/PencilIcon';
import ProductAccessIcon from 'svg/ProductAccessIcon';

import ClientClassificationsSlideout from './clientClassificationsSlideout/ClientClassificationsSlideout';
import CollaborationModal from './CollaborationModal';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const CLIENT_TYPE_OPTIONS = Object.values(ClientType).map((v) => ({
  id: v,
  name: v === ClientType.PAYOR ? 'Payer' : v,
}));

// ---------------------------------------------------------------------------
// Clients tab
// ---------------------------------------------------------------------------

const CLIENT_INCLUDES = ['managedProviderTypes', 'providedProviderTypes'] as const;

const clientsConfig: AdminListPageConfig<Client<AdminClientIndexFields<(typeof CLIENT_INCLUDES)[number]>>> = {
  title: 'Clients',
  queryKey: adminClientsQueryKeys.index(),
  fetchFn: (params, signal) => adminIndexClients(params, signal, [...CLIENT_INCLUDES]),
  includes: CLIENT_INCLUDES,
  fetchParams: { defaultSort: 'name asc' },
  searchPlaceholder: 'Search Clients',
  showResultCount: true,

  addButton: {
    label: 'Add Client',
    path: '/admin/clients/new',
    permission: 'adminClientCreate',
  },

  filters: [
    {
      key: 'clientType',
      label: 'Client Type',
      type: 'dropdown',
      staticOptions: CLIENT_TYPE_OPTIONS,
      getOptionLabel: (o) => o.name,
      getOptionValue: (o) => o.id,
    },
    {
      key: 'managedProviderType',
      label: 'Managed Care Options',
      type: 'multi_select',
      fetchConfig: {
        queryKey: ['adminGroupTypes', 'provider', 'filter'],
        fetchFn: indexGroupTypes,
        params: { type: 'provider', sortBy: 'displayName asc' },
      },
      getOptionLabel: (o) => o.displayName,
      getOptionValue: (o) => o.id,
    },
    {
      key: 'providedProviderType',
      label: 'Provided Care Options',
      type: 'multi_select',
      fetchConfig: {
        queryKey: ['adminGroupTypes', 'provider', 'filter'],
        fetchFn: indexGroupTypes,
        params: { type: 'provider', sortBy: 'displayName asc' },
      },
      getOptionLabel: (o) => o.displayName,
      getOptionValue: (o) => o.id,
    },
    { key: 'ssoEnabled', label: 'SSO Enabled', type: 'checkbox' },
    { key: 'mfaEnabled', label: 'MFA Enabled', type: 'checkbox' },
  ],

  columns: [
    {
      id: 'name',
      header: 'Client Name',
      render: (row) => (
        <>
          <strong>{row.name}</strong>
          {row.isParent && <em style={{ fontWeight: 400 }}>&nbsp;(Parent)</em>}
        </>
      ),
    },
    {
      id: 'clientType',
      header: 'Client Type',
      render: (row) => (row.clientType === ClientType.PAYOR ? 'Payer' : row.clientType) || '—',
    },
    {
      id: 'managedProviderTypes',
      header: 'Managed Care Options',
      sortable: false,
      render: (row) =>
        row.managedProviderTypes?.length
          ? (row.managedProviderTypes as GroupType<GroupTypeFields>[]).map((x) => x.displayName).join(', ')
          : '—',
    },
    {
      id: 'ssoEnabled',
      header: 'SSO Enabled',
      render: (row) => (row.ssoEnabled ? 'Yes' : 'No'),
    },
    {
      id: 'mfaEnabled',
      header: 'MFA Enabled',
      render: (row) => (row.mfaConfig?.enabled ? 'Yes' : 'No'),
    },
  ],

  rowActions: [
    {
      label: 'Edit',
      icon: <PencilIcon width={18} height={18} />,
      permission: 'adminClientView',
      onClick: (row, { navigate }) => navigate(`/admin/clients/${row.id}/edit`),
    },
    {
      label: 'Collaborations',
      icon: <CollaborationsIcon width={18} height={18} />,
      permission: 'adminCollaborationEdit',
      onClick: (row, { openModal }) => openModal('collaboration', row),
    },
    {
      label: 'Configure Managed Care Options',
      icon: <ConfigureCareIcon width={18} height={18} />,
      permission: 'adminClientGroupTypeView',
      visible: (row) => !!row.managedProviderTypes?.length,
      onClick: (row, { navigate }) => navigate(`/admin/clients/${row.id}/group-types/edit`),
    },
    {
      label: 'Edit Contracted Plans',
      icon: <AcceptedPlansIcon width={18} height={18} />,
      visible: (row) =>
        !!(
          row.providedProviderTypes?.length &&
          (row.providedProviderTypes as GroupType<GroupTypeFields>[]).some((t) => t.config?.designatesAcceptedPlans)
        ),
      onClick: (row, { openSlideout }) => openSlideout('classifications', row),
    },
    {
      label: 'Product Access',
      icon: <ProductAccessIcon width={18} height={18} />,
      permission: 'adminClientView',
      onClick: (row, { navigate }) => navigate(`/admin/clients/${row.id}/product-access`),
    },
  ],

  customModals: {
    collaboration: {
      render: (row, _show, onClose) => <CollaborationModal client={row} onCancel={onClose} />,
    },
  },

  slideouts: {
    classifications: {
      render: (row, onClose) => (
        <ClientClassificationsSlideout clientId={row.id} clientName={row.name} handleClose={onClose} />
      ),
    },
  },
};

// ---------------------------------------------------------------------------
// Collaborations tab
// ---------------------------------------------------------------------------

const COLLABORATION_INCLUDES = [] as const;

const collaborationsConfig: AdminListPageConfig<
  Collaboration<AdminCollaborationIndexFields<(typeof COLLABORATION_INCLUDES)[number]>>
> = {
  title: 'Collaborations',
  queryKey: adminCollaborationsQueryKeys.index(),
  fetchFn: (params, signal) => adminIndexCollaborations(params, signal, [...COLLABORATION_INCLUDES]),
  includes: COLLABORATION_INCLUDES,
  fetchParams: { defaultSort: 'name asc' },
  searchPlaceholder: 'Search Clients',
  showResultCount: true,

  filters: [
    {
      key: 'physicianGroup',
      label: 'Physician Group',
      type: 'dropdown',
      fetchConfig: {
        queryKey: ['adminClients', 'physicianGroup', 'filter'],
        fetchFn: adminIndexClients,
        params: { clientType: 'Physician Group', sortBy: 'name asc' },
      },
      getOptionLabel: (o) => o.name,
      getOptionValue: (o) => o.id,
    },
    {
      key: 'healthSystem',
      label: 'Health System',
      type: 'dropdown',
      fetchConfig: {
        queryKey: ['adminClients', 'healthSystem', 'filter'],
        fetchFn: adminIndexClients,
        params: { clientType: 'Health System', sortBy: 'name asc' },
      },
      getOptionLabel: (o) => o.name,
      getOptionValue: (o) => o.id,
    },
  ],

  columns: [
    {
      id: 'name',
      header: 'Primary Owner Client',
      render: (row) => <strong>{row.name}</strong>,
    },
    {
      id: 'collaboratorNames',
      header: 'Collaborating Clients',
      sortable: false,
      render: (row) => row.collaborators?.map((c) => c.name).join(', ') || '—',
    },
  ],

  rowActions: [
    {
      label: 'Edit',
      icon: <PencilIcon width={18} height={18} />,
      permission: 'adminCollaborationEdit',
      onClick: (row, { openModal }) => openModal('collaboration', row),
    },
  ],

  customModals: {
    collaboration: {
      render: (row, _show, onClose) => <CollaborationModal client={row} onCancel={onClose} />,
    },
  },
};

// ---------------------------------------------------------------------------
// Import Configurations tab
// ---------------------------------------------------------------------------

const IMPORT_CONFIG_INCLUDES = ['client'] as const;

const importConfigsConfig: AdminListPageConfig<TypedImportConfigurationIndex<(typeof IMPORT_CONFIG_INCLUDES)[number]>> =
  {
    title: 'Import Configurations',
    queryKey: adminImportConfigurationsQueryKeys.index(),
    fetchFn: (params, signal) => adminIndexImportConfigurations(params, signal, [...IMPORT_CONFIG_INCLUDES]),
    includes: IMPORT_CONFIG_INCLUDES,
    fetchParams: { defaultSort: 'clientName asc' },
    searchPlaceholder: 'Search Clients',
    showResultCount: true,

    addButton: {
      label: 'Add Import Configuration',
      path: '/admin/clients/import-configurations/new',
      permission: 'adminImportConfigurationCreate',
    },

    filters: [
      {
        key: 'clientType',
        label: 'Client Type',
        type: 'dropdown',
        staticOptions: CLIENT_TYPE_OPTIONS,
        getOptionLabel: (o) => o.name,
        getOptionValue: (o) => o.id,
      },
    ],

    columns: [
      {
        id: 'clientName',
        header: 'Client',
        render: (row) => <strong>{row.clientName}</strong>,
      },
      {
        id: 'clientType',
        header: 'Client Type',
        render: (row) => row.clientType || '—',
      },
      {
        id: 'lastModified',
        header: 'Last Modified',
        render: (row) => (row.updatedAt ? simpleDateAtTime(row.updatedAt) : '—'),
      },
    ],

    rowActions: [
      {
        label: 'Edit Import Configuration',
        icon: <PencilIcon width={18} height={18} />,
        permission: 'adminImportConfigurationView',
        onClick: (row, { navigate }) => navigate(`/admin/clients/import-configurations/${row.id}/edit`),
      },
    ],
  };

// ---------------------------------------------------------------------------
// Tab definitions
// ---------------------------------------------------------------------------

const TABS: TabDef[] = [
  { label: 'Clients', path: '/admin/clients' },
  { label: 'Collaborations', path: '/admin/clients/collaborations', permission: 'adminCollaborationView' },
  {
    label: 'Import Configurations',
    path: '/admin/clients/import-configurations',
    permission: 'adminImportConfigurationView',
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

// Each tab gets its own component so useAdminListPage can be invoked with a
// concrete row type. A union over three different AdminListPageConfig<T> can't
// satisfy the hook's invariant T parameter without `as any`.
function ClientsTab({ currentTab }: { currentTab: string }) {
  const state = useAdminListPage(clientsConfig);
  return (
    <AdminListPageRenderer
      config={clientsConfig}
      state={state}
      tabs={{ pageTitle: 'Clients', tabDefs: TABS, currentTabLabel: currentTab }}
    />
  );
}

function CollaborationsTab({ currentTab }: { currentTab: string }) {
  const state = useAdminListPage(collaborationsConfig);
  return (
    <AdminListPageRenderer
      config={collaborationsConfig}
      state={state}
      tabs={{ pageTitle: 'Clients', tabDefs: TABS, currentTabLabel: currentTab }}
    />
  );
}

function ImportConfigsTab({ currentTab }: { currentTab: string }) {
  const state = useAdminListPage(importConfigsConfig);
  return (
    <AdminListPageRenderer
      config={importConfigsConfig}
      state={state}
      tabs={{ pageTitle: 'Clients', tabDefs: TABS, currentTabLabel: currentTab }}
    />
  );
}

export default function Clients() {
  const { pathname } = useLocation();

  const isCollaborations = pathname.includes('/collaborations');
  const isImportConfigs = pathname.includes('/import-configurations');

  const currentTab = isCollaborations ? 'Collaborations' : isImportConfigs ? 'Import Configurations' : 'Clients';

  if (isCollaborations) return <CollaborationsTab currentTab={currentTab} />;
  if (isImportConfigs) return <ImportConfigsTab currentTab={currentTab} />;
  return <ClientsTab currentTab={currentTab} />;
}
